import { use, useCallback, useEffect, useMemo, useState } from "react";
import "./App.css";
import vowelLengthPdf from "./assets/vowel_length.pdf?url";
import { PlayWord, usePlay } from "./usePlay";
import { kitList, SentenceConfig } from "./kitList";
import { getRecordControl } from "./getRecordControl";
import { VoiceDialog } from "./VoiceDialog";
import { Notes } from "./Notes";
import {
  deleteAudio,
  getManifest,
  getReadAudioUrl,
  getSummary,
  normalizeId,
  saveAudio,
  saveSummary,
  setManifest,
} from "./storage";
import { MODEL_USER } from "./consts";

type RecordButtonProps = {
  isRecording: boolean;
  onChange: (isRecording: boolean) => void;
};

type RecordedAudioState = { duration: number; isPlaying: boolean };

const RecordButton = ({ isRecording, onChange }: RecordButtonProps) => {
  const handleRecord = () => {
    onChange(!isRecording);
  };

  return (
    <button
      className={`btn secondary outline record-btn ${
        isRecording ? "recording" : ""
      }`}
      onClick={handleRecord}
    >
      {isRecording ? "■" : "●"}
    </button>
  );
};

type SentenceProps = {
  sentenceConfig: SentenceConfig;
  currentWord: PlayWord | null;
  duration: number | null;
  playWord: (word: string) => void;
  isRecording: RecordButtonProps["isRecording"];
  onChangeRecording: (word: string) => void;
  recordedAudioState?: RecordedAudioState;
  onPlayRecordedAudio: (word: string) => void;
  onDelete: (word: string) => void;
};

const Duration = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className: string;
}) => <span className={`duration ${className}`}>{children}</span>;

const Sentence = ({
  sentenceConfig,
  currentWord,
  duration,
  playWord,
  isRecording,
  onChangeRecording,
  recordedAudioState,
  onPlayRecordedAudio,
  onDelete,
}: SentenceProps) => {
  const { word, wordPhonetic, sentence, sentencePhonetic, note } =
    sentenceConfig;
  return (
    <div key={word} className="sentence">
      <h6>
        <button
          className={`btn play outline ${
            word === currentWord?.word ? "primary" : "secondary"
          }`}
          onClick={() => playWord(word)}
        >
          {word === currentWord?.word && currentWord.isPlaying ? "⏸︎" : "▶"}
        </button>
        <Duration className="example-duration">
          {duration ? `${Math.round(duration)}s` : ""}
        </Duration>
        <b className="word">{word}</b>
        <span className="word-phonetic">{wordPhonetic}</span>
        <span className="record-part">
          <RecordButton
            isRecording={isRecording}
            onChange={(updateIsRecording) =>
              // play new word or stop if recording current word
              onChangeRecording(updateIsRecording ? word : "")
            }
          />
          {recordedAudioState ? (
            <>
              <button
                className={`btn outline ${
                  recordedAudioState.isPlaying ? "primary" : "secondary"
                }`}
                onClick={() =>
                  onPlayRecordedAudio(recordedAudioState.isPlaying ? "" : word)
                }
              >
                {recordedAudioState.isPlaying ? "■" : "▶"}
              </button>
              <button
                className="btn outline secondary"
                onClick={() => onDelete(word)}
              >
                ✕
              </button>
              <span>
                <Duration className="record-play">
                  {Math.round(recordedAudioState.duration)}s
                </Duration>
              </span>
            </>
          ) : null}
        </span>
      </h6>
      <p>
        {sentence}
        <br />
        {sentencePhonetic}
        {note ? (
          <button className="question secondary outline" title={note}>
            ?
          </button>
        ) : null}
      </p>
    </div>
  );
};

type AppProps = {
  recordControl: Awaited<ReturnType<typeof getRecordControl>>;
};
function App({ recordControl }: AppProps) {
  // Should read manifest first, other functions depend on it
  const manifest = use(getManifest());
  setManifest(manifest);
  const modelSummary = use(getSummary(MODEL_USER));
  const [userSummary, setUserSummary] = useState(manifest.summary);
  const userName = manifest.user.name;

  const audioEl = useMemo(() => {
    const audio = document.createElement("audio");
    return audio;
  }, []);

  const recordAudioEl = useMemo(() => {
    const audio = document.createElement("audio");
    // need to remove the listener?
    audio.addEventListener("ended", () => {
      setPlayingRecordedWord("");
    });

    return audio;
  }, []);

  const [error, setError] = useState("");

  const [recordingWord, setRecordingWord] = useState("");
  const [currentWord, playWord, stop, toggle] = usePlay(audioEl, kitList);
  const [isVoiceDialogOpen, setIsVoiceDialogOpen] = useState(false);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);

  useEffect(() => {
    recordControl.setOnComplete(async (word, blob, durationMs) => {
      try {
        const id = normalizeId(word);

        const response = await saveAudio(id, blob);
        const durationAndVersion = {
          duration: durationMs / 1000,
          version: response.version,
        };
        const newSummary = {
          ...userSummary,
          [id]: durationAndVersion,
        };
        // hack mutating userSummary so that after record then hit play, we have the latest version
        userSummary[id] = durationAndVersion;

        await saveSummary(newSummary);
        setUserSummary(newSummary);
        // the case where the recording reaches the max duration limit
        if (id === recordingWord) {
          setRecordingWord("");
        }
      } catch (e) {
        setError(`Cannot save audio. ${(e as Error).message}`);
      }
    });
  }, [recordControl, recordingWord, userSummary]);

  useEffect(() => {
    const controller = new AbortController();
    document.addEventListener(
      "keydown",
      (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          setIsVoiceDialogOpen(false);
          setIsNotesDialogOpen(false);
        }
      },
      {
        signal: controller.signal,
      }
    );
    return () => controller.abort();
  }, []);

  const stopAll = async () => {
    stop();
    setRecordingWord("");
    await recordControl.stop();
    setPlayingRecordedWord("");
    recordAudioEl.pause();
  };

  useEffect(() => {
    // this helps clearing state when using hotkey, not clicking on button
    if (currentWord) {
      setRecordingWord("");
      // no need to await here because for hotkey, we are not playing a recorded sound
      void recordControl.stop();
      setPlayingRecordedWord("");
      recordAudioEl.pause();
    }
  }, [currentWord]);

  const onPlay = useCallback(
    (word: string) => {
      if (word === currentWord?.word) {
        toggle();
      } else {
        stopAll();
        playWord(word);
      }
    },
    [playWord, stopAll]
  );

  const onChangeRecording = useCallback(
    async (newWord: string) => {
      await stopAll();
      if (newWord) {
        recordControl.record(newWord);
      }
      setRecordingWord(newWord);
    },
    [stopAll, recordControl]
  );

  const [playingRecordedWord, setPlayingRecordedWord] = useState("");
  const onPlayRecordedAudio = async (newWord: string) => {
    await stopAll();
    if (newWord) {
      const version = userSummary[normalizeId(newWord)].version;
      const audioUrl = getReadAudioUrl(userName, newWord, version);
      recordAudioEl.src = audioUrl;
      recordAudioEl.currentTime = 0;
      recordAudioEl.play();
    }
    setPlayingRecordedWord(newWord);
  };
  const onDeleteRecord = async (word: string) => {
    if (playingRecordedWord === word) {
      setPlayingRecordedWord("");
      recordAudioEl.pause();
    }
    try {
      const id = normalizeId(word);
      const newSummary = { ...userSummary };
      delete newSummary[id];
      await deleteAudio(id);
      await saveSummary(newSummary);
      setUserSummary(newSummary);
    } catch (e) {
      setError(`Cannot remove audio. ${(e as Error).message}`);
    }
  };

  return (
    <>
      <header className="container">
        <hgroup>
          <h1>
            <div>{kitList.title}</div>
          </h1>
          <p>
            Logged in as: <b>{userName}</b>
            <a href="/api/logout" className="btn-logout">
              Logout
            </a>
          </p>
          <p>
            Hit <b>Space</b> to Play/Pause. <b>Enter</b> to rewind.
            <br />
            <b>n</b> or <b>p</b> to play next or previous word.
          </p>
          <p>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setIsVoiceDialogOpen(true);
              }}
            >
              Show voice/voiceless consonants
            </a>
            ,
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setIsNotesDialogOpen(true);
              }}
            >
              Notes
            </a>
            ,
            <a href={vowelLengthPdf} target="_blank">
              Vowel length
            </a>
          </p>
        </hgroup>
      </header>
      {error ? <p className="container error">{error}</p> : null}
      <main className="container">
        {kitList.sections.map(({ title, sentences }) => (
          <section key={title}>
            <h3>{title}</h3>
            {sentences.map((sentenceConfig) => {
              const { word } = sentenceConfig;
              const recordedDurationAndVersion = userSummary[normalizeId(word)];
              let recordedAudioState: RecordedAudioState | undefined =
                undefined;
              if (recordedDurationAndVersion) {
                recordedAudioState = {
                  duration: recordedDurationAndVersion.duration,
                  isPlaying: playingRecordedWord === word,
                };
              }
              return (
                <Sentence
                  key={sentenceConfig.word}
                  sentenceConfig={sentenceConfig}
                  currentWord={currentWord}
                  duration={
                    modelSummary?.[normalizeId(sentenceConfig.word)].duration
                  }
                  playWord={onPlay}
                  isRecording={recordingWord === sentenceConfig.word}
                  onChangeRecording={onChangeRecording}
                  recordedAudioState={recordedAudioState}
                  onPlayRecordedAudio={onPlayRecordedAudio}
                  onDelete={onDeleteRecord}
                />
              );
            })}
          </section>
        ))}
      </main>
      <VoiceDialog open={isVoiceDialogOpen} setOpen={setIsVoiceDialogOpen} />
      <Notes open={isNotesDialogOpen} setOpen={setIsNotesDialogOpen} />
    </>
  );
}

export default App;
